import { z } from 'zod';

// =============================================================================
//  Coupon schemas — Cupons de desconto no checkout
//  Spec: docs/specs/checkout-coupons.spec.md
//  Cupom reutilizável digitado no checkout público; desconto na 1ª cobrança.
// =============================================================================

export const couponDiscountTypeSchema = z.enum(['percent', 'fixed']);
export type CouponDiscountTypeSchema = z.infer<typeof couponDiscountTypeSchema>;

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

const CODE_REGEX = /^[A-Za-z0-9_-]+$/;
const codeField = z
  .string()
  .min(4, 'Código deve ter ao menos 4 caracteres.')
  .max(50, 'Código deve ter até 50 caracteres.')
  .regex(CODE_REGEX, 'Código aceita só letras, números, hífen e underscore.')
  .transform((s) => s.toUpperCase());

const positiveOptionalDecimal = (label: string) =>
  z.coerce
    .number({ invalid_type_error: `${label} deve ser numérico.` })
    .positive(`${label} deve ser > 0.`)
    .optional()
    .nullable();

/**
 * Por quantos CICLOS de cobrança o desconto do cupom vale.
 *
 * `null`/ausente = só a primeira cobrança (é o que todo cupom sempre fez).
 * Um número = também vale nas renovações seguintes, virando desconto na própria
 * assinatura no ato da compra (public-checkout.service.ts, passo 7.6).
 *
 * O teto de 600 (50 anos) não é generosidade: é o guarda contra alguém digitar
 * 99999 e criar um desconto que nenhuma revisão futura vai alcançar. Para
 * "vitalício", a UI usa 240 (20 anos) — longo o bastante na prática, e com data
 * visível na assinatura para que alguém um dia revise.
 */
const couponDurationMonths = z.coerce
  .number({ invalid_type_error: 'Duração do desconto deve ser numérica.' })
  .int('Duração do desconto deve ser em meses inteiros.')
  .positive('Duração do desconto deve ser >= 1 mês.')
  .max(600, 'Duração do desconto não pode passar de 600 meses (50 anos).')
  .optional()
  .nullable();

const isoDateTime = (label: string) =>
  z
    .string()
    .datetime({ offset: true, message: `${label} deve ser uma data ISO válida.` })
    .optional()
    .nullable();

// Cross-field shared por CREATE e UPDATE (quando os campos relevantes vêm no payload).
const couponCrossRules = (
  data: {
    discount_type?: 'percent' | 'fixed';
    discount_value?: number;
    applies_to_all_plans?: boolean;
    plan_ids?: string[];
    valid_from?: string | null;
    valid_until?: string | null;
  },
  ctx: z.RefinementCtx,
): void => {
  // Percentual não passa de 100%.
  if (
    data.discount_type === 'percent' &&
    data.discount_value !== undefined &&
    data.discount_value > 100
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['discount_value'],
      message: 'Percentual não pode passar de 100.',
    });
  }
  // Escopo: se não vale pra todos os planos, precisa listar planos.
  if (data.applies_to_all_plans === false && (data.plan_ids?.length ?? 0) === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['plan_ids'],
      message: 'Selecione ao menos 1 plano ou marque "vale pra todos os planos".',
    });
  }
  // Janela de validade coerente.
  if (data.valid_from && data.valid_until && data.valid_until <= data.valid_from) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['valid_until'],
      message: 'Validade final deve ser depois da inicial.',
    });
  }
};

// -----------------------------------------------------------------------------
// CREATE
// -----------------------------------------------------------------------------

export const createCouponSchema = z
  .object({
    code: codeField,
    description: z
      .string()
      .max(500, 'Descrição deve ter até 500 caracteres.')
      .optional()
      .nullable(),
    discount_type: couponDiscountTypeSchema,
    discount_value: z.coerce
      .number({ invalid_type_error: 'Valor do desconto deve ser numérico.' })
      .positive('Valor do desconto deve ser > 0.'),
    max_discount: positiveOptionalDecimal('Teto do desconto'),
    applies_to_all_plans: z.coerce.boolean().default(false),
    plan_ids: z.array(z.string().uuid('plan_id inválido.')).default([]),
    usage_limit: z.coerce
      .number()
      .int()
      .positive('Limite de uso deve ser >= 1.')
      .optional()
      .nullable(),
    valid_from: isoDateTime('Início da validade'),
    valid_until: isoDateTime('Fim da validade'),
    min_purchase: positiveOptionalDecimal('Compra mínima'),
    discount_duration_months: couponDurationMonths,
  })
  .strict()
  .superRefine(couponCrossRules);
export type CreateCouponDto = z.output<typeof createCouponSchema>;
export type CreateCouponInput = z.input<typeof createCouponSchema>;

// -----------------------------------------------------------------------------
// UPDATE — `code`, `discount_type` e `discount_value` são IMUTÁVEIS após criação
// (cupom já pode ter sido divulgado). Só campos operacionais mudam.
// -----------------------------------------------------------------------------

export const updateCouponSchema = z
  .object({
    description: z.string().max(500).optional().nullable(),
    is_active: z.coerce.boolean().optional(),
    max_discount: positiveOptionalDecimal('Teto do desconto'),
    applies_to_all_plans: z.coerce.boolean().optional(),
    plan_ids: z.array(z.string().uuid('plan_id inválido.')).optional(),
    usage_limit: z.coerce.number().int().positive().optional().nullable(),
    valid_from: isoDateTime('Início da validade'),
    valid_until: isoDateTime('Fim da validade'),
    min_purchase: positiveOptionalDecimal('Compra mínima'),
    // Editável depois de criado, ao contrário de `discount_value`: mudar a
    // DURAÇÃO não altera o que quem já resgatou vai pagar (o desconto deles já
    // foi gravado na assinatura no ato da compra), só o que os próximos pegam.
    discount_duration_months: couponDurationMonths,
  })
  .strict()
  .superRefine((data, ctx) => {
    // Só valida escopo se o campo veio no payload (PATCH parcial).
    if (data.applies_to_all_plans !== undefined || data.plan_ids !== undefined) {
      couponCrossRules(
        { applies_to_all_plans: data.applies_to_all_plans ?? false, plan_ids: data.plan_ids ?? [] },
        ctx,
      );
    }
    if (data.valid_from && data.valid_until && data.valid_until <= data.valid_from) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['valid_until'],
        message: 'Validade final deve ser depois da inicial.',
      });
    }
  });
export type UpdateCouponDto = z.output<typeof updateCouponSchema>;
export type UpdateCouponInput = z.input<typeof updateCouponSchema>;

// -----------------------------------------------------------------------------
// QUERY — GET /api/coupons
// -----------------------------------------------------------------------------

export const couponStatusFilterSchema = z.enum(['active', 'inactive', 'expired', 'exhausted']);

export const queryCouponsSchema = z.object({
  status: couponStatusFilterSchema.optional(),
  q: z.string().min(1).max(100).optional(),
  cursor: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type QueryCouponsDto = z.infer<typeof queryCouponsSchema>;

// -----------------------------------------------------------------------------
// VALIDATE — POST /api/public/checkout/validate-coupon (público, preview)
// -----------------------------------------------------------------------------

export const validateCouponSchema = z
  .object({
    code: z.string().min(1, 'Código obrigatório.').max(50),
    plan_slug: z.string().min(1, 'plan_slug obrigatório.'),
    saas_origin: z.string().optional(),
    // `pagamento_unico` (peça 4): plano de serviço de ciclo único (implantação).
    billing_cycle: z.enum(['mensal', 'anual', 'pagamento_unico']).optional().default('mensal'),
    email: z.string().email('email inválido.').optional(),
  })
  .strict();
export type ValidateCouponDto = z.output<typeof validateCouponSchema>;
export type ValidateCouponInput = z.input<typeof validateCouponSchema>;
